import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import { CommunityPac } from "../../types/community/community-pac.type";
import { Community } from "../../types/community/community.type";
import { User } from "../../types/user/minimal-user.type";
import { Button, ErrorDiv, TextInput } from "../../_components";
import { ActionModelTrigger } from "../../_components/action-modal.component";
import PremiumFeature from "../../_components/premium/premium-feature.component";
import { CustomSubdomainService } from "../../_service/custom-subdomain.service";
import { setCommunity } from "../../_store/_actions/community.actions";
import { useAppService } from "../../hooks/use-app-service";

export const COMMUNITY_SETTING_CUSTOM_DOMAIN_PAGE_ROUTE =
  "/settings/custom-domains";

interface Props {
  user: User;
  community: Community;
  communityPac: CommunityPac;
  addToast: any;
}

export function CommunitySettingsCustomDomainPageComponent({
  user,
  community,
  communityPac,
  addToast,
}: Props) {
  const [requests, setRequests] = useState<any[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);

  const communityId = community?.id;

  useEffect(() => {
    setIsLoadingRequests(true);
    CustomSubdomainService.getAllRequests(communityId)
      .then(({ requests }) => {
        setRequests(requests);
      })
      .catch((error) => {
        addToast("Could not load requests!", "", ToastTypes.danger);
      })
      .finally(() => {
        setIsLoadingRequests(false);
      });
  }, [communityId]);

  const addRequest = (request: any) => {
    setRequests([...requests, request]);
  };

  const deleteRequest = (request: any) => {
    setRequests(requests.filter((r) => r.id !== request.id));
  };

  return (
    <PremiumFeature isAvailable={communityPac.customDomain} hideFeature>
      <div className="CommunitySettingCustomDomianPage Card rounded shadow theme-bg-surface">
        <div className="main-content ">
          {/* header card */}
          <div className="pl-4 pr-2 py-4  border-b theme-border-default flex items-center justify-between">
            <div className="font-bold theme-text-heading-2">Custom Domain</div>
            <Button
              label="+ Add Custom Domain"
              onClick={(e) => {
                //
              }}
            />
          </div>
          <div className="py-2 px-4">
            <div className="font-semibold mb-2 text-lg capitalize mt-2">
              Create new request
            </div>
            <SubdomainCard
              community={community}
              addToast={addToast}
              addRequest={addRequest}
              request={undefined}
            />
            <div className="font-semibold mb-2 text-lg capitalize mt-2">
              All requests list
            </div>
            {/* custom domain card */}
            {requests.map((request, index) => (
              <SubdomainCard
                key={index}
                request={request}
                community={community}
                deleteRequest={deleteRequest}
              />
            ))}
          </div>
        </div>
      </div>
    </PremiumFeature>
  );
}

interface SubdomainCardProps {
  request: any;
  community: Community;
  addToast?: any;
  addRequest?: (request: Request) => void;
  deleteRequest?: (request: Request) => void;
}
const SubdomainCard = ({
  request,
  community,
  addToast,
  addRequest = (e) => {},
  deleteRequest = (e) => {},
}: SubdomainCardProps) => {
  const [customDomain, setCustomDomain] = useState<string>("");
  const [error, setError] = useState<undefined | string>();
  const [isLoadingCreate, setIsLoadingCreate] = useState(false);

  const { analyticsService } = useAppService();

  // Check if the environment variable is defined and assign a default value if not
  const mainDomainSuffix = process.env.REACT_APP_MAIN_DOMAIN_SUFFIX || "";
  const [isDeleteRequest, setDeleteRequest] = useState(false);

  const isCustomSubdomain = request?.customDomain?.split(".").length > 1;

  return (
    <div className="SubdomainCard border theme-border-default px-3 py-2 rounded mb-4">
      {/* header */}
      <div className="flex justify-between items-center">
        <div className="font-semibold">Enter your custom domain</div>
        {request ? (
          <ActionModelTrigger
            header="Are you sure you want to remove this request?"
            isLoading={isDeleteRequest}
            onSubmit={(e) => {
              setDeleteRequest(true);
              CustomSubdomainService.deleteRequest(community.id, request.id)
                .then(() => {
                  analyticsService.track("custom-domain-removed", {
                    customDomain: request.customDomain
                  })
                  // addToast("Request deleted!", "", ToastTypes.success);
                  deleteRequest(request);
                })
                .catch((error) => {
                  // addToast("Could not delete request!", "", ToastTypes.danger);
                })
                .finally(() => {
                  setDeleteRequest(false);
                });
            }}
            component={
              <div className="text-red-500 font-bold text-sm cursor-pointer">
                Remove
              </div>
            }
            children={undefined}
          />
        ) : (
          <></>
        )}
      </div>
      {/* body */}
      <TextInput
        placeholder="Eg. community.example.com"
        large
        readOnly={request}
        defaultValue={
          request?.customDomain ? request.customDomain : customDomain
        }
        onChange={(value) => {
          setCustomDomain(value);
        }}
        validationOutsideBox
        validator={(value) => {
          if (value.length === 0) {
            return "Please enter a custom domain";
          }
          // regex for domain or sub domain
          if (
            !/^([a-zA-Z0-9][a-zA-Z0-9-]{1,61})([a-zA-Z0-9]\.[a-zA-Z]{1,})?([a-zA-Z0-9]\.[a-zA-Z]{2,})$/.test(
              value
            )
          ) {
            return "Invalid domain";
          }

          if (
            value.toLowerCase().endsWith(mainDomainSuffix) ||
            value.toLowerCase().endsWith("pensil.in") ||
            value.toLowerCase().endsWith("pensil.so") ||
            value.toLowerCase().endsWith("pensil.com")
          ) {
            return "Custom domain cannot be the same as pensil/current";
          }
        }}
        // @ts-ignore
        postfix={
          request ? (
            request.isAccepted ? (
              <div className="text-green-500 font-semibold text-sm mr-3">
                Completed
              </div>
            ) : (
              <div className="text-yellow-500 font-semibold text-sm mr-3">
                Requested
              </div>
            )
          ) : null
        }
      />
      <ErrorDiv error={error} />
      {/* show setup details */}

      {/* setup details */}

      <div className="mt-5">
        <div>
          Add a CNAME record in your domain pointing to this community address
        </div>
        <table className="w-full my-4 border-separate theme-border-default">
          <thead>
            <tr>
              <th>
                <div className="text-left p-2">Type</div>
              </th>
              <th>
                <div className="text-left p-2">Host/Name</div>
              </th>
              <th>
                <div className="text-left p-2">Value</div>
              </th>
            </tr>
          </thead>
          <tbody className="rounded border theme-border-default">
            <tr className="border theme-border-default rounded">
              <td className="p-2 border rounded-l border-r-0 theme-border-default">
                <div>CNAME</div>
              </td>
              <td className="p-2 border border-r-0 border-l-0 theme-border-default">
                <div>
                  {request?.customDomain ? (
                    request?.customDomain.split(".")[0]
                  ) : customDomain ? (
                    customDomain
                  ) : (
                    <div className="theme-text-subtitle-1">community</div>
                  )}
                </div>
              </td>
              <td className="p-2 border rounded-r border-l-0 theme-border-default">
                <div>
                  {community.address}
                  {process.env.REACT_APP_MAIN_DOMAIN_SUFFIX}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="theme-text-subtitle-1 text-xs">
          To configure your domain, you will need to add a CNAME record pointing
          your domain to your Pensil community URL
        </div>
        <div className="mt-1">
          <a
            href="https://team.pensil.in/p/host-your-community-on-your-own-custom-domain-or-subdomain"
            className="text-xs">
            Step by step Help Page
          </a>
        </div>
      </div>

      {/* add message button */}
      {!request ? (
        <div className="flex justify-between items-center mt-2">
          <Button
            flat
            label="Cancel"
            onClick={(e) => {
              setCustomDomain("");
              setError("");
            }}
          />
          <Button
            label="Save"
            isLoading={isLoadingCreate}
            disabled={customDomain.length === 0}
            onClick={(e) => {
              // call the community update api
              setError("");
              setIsLoadingCreate(true);
              CustomSubdomainService.createRequest(community.id, customDomain)
                .then(({ request }) => {
                  analyticsService.track("custom-domain-updated", {
                    customDomain: request.customDomain
                  })
                  addRequest(request);
                  setCustomDomain("");                
                  addToast("Request created successfully!");
                })
                .catch((error) => {
                  setError(error?.response?.data?.message);
                  addToast("Could not create request!", "", ToastTypes.danger);
                })
                .finally(() => {
                  setIsLoadingCreate(false);
                });
            }}
          />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

const CommunitySettingsCustomDomainPage = withToast(
  connect(
    (s: any) => ({
      community: s.community,
      user: s.auth,
      communityPac: s.communityPac,
    }),
    (d: any) =>
      bindActionCreators(
        {
          updateCommunity: setCommunity,
        },
        d
      )
  )(CommunitySettingsCustomDomainPageComponent)
);

export default CommunitySettingsCustomDomainPage;
